import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, ExternalLink, Plus, Archive, ArchiveRestore, RotateCcw, FileText, Mail, Calendar, Save, Zap } from 'lucide-react';
import { driverApi, DriverUser, DriverProfile, DriverEvent, Partner } from '../../services/driverApi';

interface UserDetailsModalProps {
  user: DriverUser;
  onClose: () => void;
  onAddEsim: () => void;
  onUserUpdate?: () => void;
}

export const UserDetailsModal: React.FC<UserDetailsModalProps> = ({ user, onClose, onAddEsim, onUserUpdate }) => {
  const [userDetails, setUserDetails] = useState<DriverUser & { profiles: DriverProfile[]; events: DriverEvent[] } | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingDueDate, setEditingDueDate] = useState<string | null>(null);
  const [newDueDate, setNewDueDate] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [topupPackages, setTopupPackages] = useState<any[]>([]);
  const [selectedTopupPackage, setSelectedTopupPackage] = useState<string>('');
  const [loadingTopupPackages, setLoadingTopupPackages] = useState(false);
  const [topupError, setTopupError] = useState<string>('');
  const [topupSubmitting, setTopupSubmitting] = useState(false);
  const [renewalPlans, setRenewalPlans] = useState<{[key: string]: any}>({});

  useEffect(() => {
    loadUserDetails();
  }, [user.name]);

  const loadUserDetails = async () => {
    setLoading(true);
    try {
      const response = await driverApi.getUser(user.name);
      // Handle the response structure: { user, profiles, events }
      const details = {
        ...response.user,
        profiles: response.profiles || [],
        events: response.events || []
      };
      setUserDetails(details);
      
      // Load renewal plans for each profile
      if (details.profiles) {
        for (const profile of details.profiles) {
          await loadRenewalPlan(profile.name);
        }
      }
    } catch (error) {
      console.error('Failed to load user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRenewalPlan = async (profileName: string) => {
    try {
      const response = await driverApi.getRenewalTopupPlan(profileName);
      if (response.ok) {
        setRenewalPlans(prev => ({
          ...prev,
          [profileName]: response.plan
        }));
      }
    } catch (error) {
      console.error(`Failed to load renewal plan for profile ${profileName}:`, error);
    }
  };

  // Check if user has an active eSIM
  const hasActiveEsim = () => {
    if (!userDetails?.profiles) return false;
    return userDetails.profiles.some(profile => profile.status === 'ACTIVE');
  };

  const handleArchiveUser = async () => {
    try {
      await driverApi.archiveUser(user.name);
      if (onUserUpdate) {
        onUserUpdate();
      }
      onClose();
    } catch (error) {
      console.error('Failed to archive user:', error);
    }
  };

  const handleUnarchiveUser = async () => {
    try {
      await driverApi.unarchiveUser(user.name);
      if (onUserUpdate) {
        onUserUpdate();
      }
      onClose();
    } catch (error) {
      console.error('Failed to unarchive user:', error);
    }
  };

  const handleEditDueDate = (profile: DriverProfile) => {
    setEditingDueDate(profile.name);
    setNewDueDate(profile.due_date ? new Date(profile.due_date).toISOString().split('T')[0] : '');
  };

  const handleSaveDueDate = async (profile: DriverProfile) => {
    if (!newDueDate) return;
    
    setSaving(true);
    try {
      await driverApi.setDueDate(profile.name, newDueDate);
      console.log('Due date updated successfully');
      setEditingDueDate(null);
      await loadUserDetails(); // Reload to get updated data
    } catch (error: any) {
      console.error('Failed to update due date:', error);
      alert(error.message || 'Failed to update due date');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingDueDate(null);
    setNewDueDate('');
  };

  const handleAutoRenewChange = async (profile: DriverProfile, isAutoRenew: boolean) => {
    try {
      setSaving(true);
      await driverApi.updateAutoRenew(profile.name, isAutoRenew);
      
      // Update the profile in the local state
      setUserDetails(prev => ({
        ...prev,
        profiles: prev.profiles.map(p => 
          p.name === profile.name 
            ? { ...p, is_auto_renew: isAutoRenew }
            : p
        )
      }));
      
      alert(`Auto-renew ${isAutoRenew ? 'enabled' : 'disabled'} for profile ${profile.name}`);
    } catch (error) {
      console.error('Failed to update auto-renew setting:', error);
      alert('Failed to update auto-renew setting');
    } finally {
      setSaving(false);
    }
  };

  const handleViewPdf = async (profile: DriverProfile) => {
    if (!profile.pdf_file) {
      alert('No PDF file available for this profile');
      return;
    }

    try {
      // Download the PDF content
      const pdfData = await driverApi.downloadPdf(profile.pdf_file.download_url.split('file_name=')[1]);
      
      // Convert base64 to blob
      const byteCharacters = atob(pdfData.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      // Create object URL and open in new tab
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Clean up the object URL after a delay
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error) {
      console.error('Failed to view PDF:', error);
      alert('Failed to open PDF. Please try again.');
    }
  };

  const handleResendEmail = async (profile: DriverProfile) => {
    try {
      const result = await driverApi.resendEmail(profile.name);
      alert(result.message);
      // Reload user details to get updated events
      await loadUserDetails();
    } catch (error: any) {
      console.error('Failed to resend email:', error);
      alert(error.message || 'Failed to resend email. Please try again.');
    }
  };

  const loadTopupPackages = async (iccid: string) => {
    setLoadingTopupPackages(true);
    setTopupError('');
    try {
      const response = await driverApi.getTopupPackages(iccid);
      if (response.success && response.packages) {
        setTopupPackages(response.packages);
        if (response.packages.length > 0) {
          setSelectedTopupPackage(response.packages[0].product_name);
        }
      } else {
        setTopupError(response.error || 'Failed to load top-up packages');
      }
    } catch (err: any) {
      setTopupError(err.message || 'Failed to load top-up packages');
    } finally {
      setLoadingTopupPackages(false);
    }
  };

  const handleTopupSubmit = async (profile: DriverProfile) => {
    if (!selectedTopupPackage) return;

    setTopupSubmitting(true);
    setTopupError('');
    try {
      const response = await driverApi.setRenewalTopupPackage(profile.name, selectedTopupPackage);
      if (response.ok) {
        alert(response.message);
        // Reload the renewal plan for this specific profile
        await loadRenewalPlan(profile.name);
        setSelectedTopupPackage('');
        setTopupPackages([]);
      } else {
        setTopupError('Failed to save renewal top-up plan. Please try again.');
      }
    } catch (error: any) {
      setTopupError(error.message || 'Failed to save renewal top-up plan. Please try again.');
    } finally {
      setTopupSubmitting(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusClasses: { [key: string]: string } = {
      'active': 'bg-green-100 text-green-800',
      'inactive': 'bg-gray-100 text-gray-800',
      'not_active': 'bg-gray-100 text-gray-800',
      'suspended': 'bg-yellow-100 text-yellow-800',
      'expired': 'bg-red-100 text-red-800',
      'pending': 'bg-blue-100 text-blue-800',
      'failed': 'bg-red-100 text-red-800',
      'canceled': 'bg-gray-100 text-gray-800',
      'requested': 'bg-yellow-100 text-yellow-800',
      'ACTIVE': 'bg-green-100 text-green-800',
      'INACTIVE': 'bg-gray-100 text-gray-800',
      'NOT_ACTIVE': 'bg-gray-100 text-gray-800',
      'SUSPENDED': 'bg-yellow-100 text-yellow-800',
      'EXPIRED': 'bg-red-100 text-red-800',
      'PENDING': 'bg-blue-100 text-blue-800',
      'FAILED': 'bg-red-100 text-red-800',
      'CANCELED': 'bg-gray-100 text-gray-800',
      'REQUESTED': 'bg-yellow-100 text-yellow-800',
      'Pending': 'bg-blue-100 text-blue-800',
      'Active': 'bg-green-100 text-green-800',
      'Not Active': 'bg-gray-100 text-gray-800',
      'Suspended': 'bg-yellow-100 text-yellow-800',
      'Failed': 'bg-red-100 text-red-800',
      'Canceled': 'bg-gray-100 text-gray-800',
      'Requested': 'bg-yellow-100 text-yellow-800',
      'ARCHIVED': 'bg-gray-100 text-gray-800'
    };
    
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status.toUpperCase()}
      </span>
    );
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'user_created':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-green-500">
            <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
            <circle cx="9" cy="7" r="4"></circle>
            <line x1="19" x2="19" y1="8" y2="14"></line>
            <line x1="22" x2="16" y1="11" y2="11"></line>
          </svg>
        );
      case 'user_archived':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-gray-500">
            <rect width="20" height="5" x="2" y="3" rx="1"></rect>
            <path d="M4 8v11a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8"></path>
            <path d="M10 12h4"></path>
          </svg>
        );
      case 'esim_issued':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-purple-500">
            <path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"></path>
            <path d="M12 22V12"></path>
            <polyline points="3.29 7 12 12 20.71 7"></polyline>
            <path d="m7.5 4.27 9 5.15"></path>
          </svg>
        );
      case 'email_sent':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-indigo-500">
            <path d="m22 7-8.991 5.727a2 2 0 0 1-2.009 0L2 7"></path>
            <rect x="2" y="4" width="20" height="16" rx="2"></rect>
          </svg>
        );
      case 'automatic_topup':
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-green-500">
            <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"></path>
            <path d="M21 3v5h-5"></path>
            <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"></path>
            <path d="M8 16H3v5"></path>
          </svg>
        );
      default:
        return (
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5 text-gray-500">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
            <polyline points="14,2 14,8 20,8"></polyline>
            <line x1="16" x2="8" y1="13" y2="13"></line>
            <line x1="16" x2="8" y1="17" y2="17"></line>
            <polyline points="10,9 9,9 8,9"></polyline>
          </svg>
        );
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500 mx-auto"></div>
          <p className="mt-4 text-center text-gray-600">Loading user details...</p>
        </div>
      </div>
    );
  }

  if (!userDetails) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <p className="text-center text-gray-600">Failed to load user details</p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <button
                onClick={onClose}
                className="text-gray-500 hover:text-gray-700 mr-4"
              >
                <ArrowLeft className="h-5 w-5" />
              </button>
              <h1 className="text-2xl font-bold text-gray-900">User Details</h1>
            </div>
            <button
              onClick={userDetails.status === 'ACTIVE' ? handleArchiveUser : handleUnarchiveUser}
              className={`px-4 py-2 rounded-md flex items-center ${
                userDetails.status === 'ACTIVE'
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-green-600 text-white hover:bg-green-700'
              }`}
            >
              {userDetails.status === 'ACTIVE' ? (
                <>
                  <Archive className="h-4 w-4 mr-2" />
                  Archive User
                </>
              ) : (
                <>
                  <ArchiveRestore className="h-4 w-4 mr-2" />
                  Unarchive User
                </>
              )}
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column */}
            <div className="lg:col-span-1 space-y-6">
              {/* User Details Card */}
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-gray-900">
                    {userDetails.first_name} {userDetails.last_name}
                  </h2>
                  {getStatusBadge(userDetails.status)}
                </div>
                
                <div className="space-y-3">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Email:</span> {userDetails.email}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Phone:</span> {userDetails.phone || '-'}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Partner:</span> {userDetails.partner ? (
                      <div>
                        <div className="font-medium">{userDetails.partner.name}</div>
                        {userDetails.partner.code && (
                          <div className="text-xs text-gray-500">{userDetails.partner.code}</div>
                        )}
                      </div>
                    ) : '-'}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">External ID:</span> {userDetails.external_driver_id || '-'}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">MeetNGreet:</span>{' '}
                    {userDetails.external_driver_id ? (
                      <a
                        href={`https://meetngreet.example.com/users/${userDetails.external_driver_id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 flex items-center inline-flex"
                      >
                        View <ExternalLink className="h-3 w-3 ml-1" />
                      </a>
                    ) : (
                      <span className="text-gray-500">Not available</span>
                    )}
                  </div>
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">Created:</span> {new Date(userDetails.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </div>
                </div>
              </div>

              {/* Current Subscription Card */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Current Subscription</h3>
                {userDetails.profiles && userDetails.profiles.length > 0 ? (
                  <div className="space-y-4">

                    {userDetails.profiles.map((profile) => {
                      // Get status from esim_profile.usage.status (ESIM Usage status from Airalo API) if available
                      const usageStatus = profile.esim_profile?.usage?.status;
                      const displayStatus = usageStatus || profile.status || 'PENDING';
                      
                      return (
                      <div key={profile.name}>
                        <div className="text-sm font-medium text-gray-900 mb-2">
                          {profile.item_code || 'eSIM Package'}
                        </div>
                        <div className="mb-3">
                          {getStatusBadge(displayStatus)}
                        </div>
                        
                        <div className="space-y-2 text-sm text-gray-600">
                          <div>
                            <span className="font-medium">Start Date</span>
                            <div>
                              {profile.activation_date 
                                ? new Date(profile.activation_date).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
                                : '-'
                              }
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Due Date</span>
                            {editingDueDate === profile.name ? (
                              <div className="flex items-center space-x-2 mt-1">
                                <input
                                  type="date"
                                  value={newDueDate}
                                  onChange={(e) => setNewDueDate(e.target.value)}
                                  className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  disabled={saving}
                                />
                                <button
                                  onClick={() => handleSaveDueDate(profile)}
                                  disabled={saving || !newDueDate}
                                  className="p-1 text-green-600 hover:text-green-800 disabled:opacity-50"
                                >
                                  <Save className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  disabled={saving}
                                  className="p-1 text-gray-600 hover:text-gray-800 disabled:opacity-50"
                                >
                                  <X className="h-4 w-4" />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <div className="text-sm text-gray-900">
                                  {profile.due_date ? new Date(profile.due_date).toLocaleDateString('en-GB', { 
                                    day: '2-digit', 
                                    month: '2-digit', 
                                    year: 'numeric' 
                                  }) : '-'}
                                </div>
                                <button
                                  onClick={() => handleEditDueDate(profile)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                  title="Edit due date"
                                >
                                  <Calendar className="h-4 w-4" />
                                </button>
                              </div>
                            )}
                          </div>
                          <div>
                            <span className="font-medium">Current Top-Up Plan</span>
                            <div className="mt-1">
                              {renewalPlans[profile.name] ? (
                                <div className="bg-blue-50 p-2 rounded text-sm">
                                  <div className="font-medium text-blue-900">
                                    {renewalPlans[profile.name].product_title || renewalPlans[profile.name].product_name}
                                  </div>
                                  <div className="text-blue-700 text-xs">
                                    {renewalPlans[profile.name].data_amount}MB - {renewalPlans[profile.name].validity_days} days - €{renewalPlans[profile.name].gross_price_eur}
                                  </div>
                                  <div className="text-blue-600 text-xs">
                                    Airalo ID: {renewalPlans[profile.name].airalo_package_id}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-gray-500 text-sm">No renewal plan set</div>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Top-Up Plan</span>
                            <div className="mt-1">
                              {topupError && (
                                <div className="mb-2 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-xs">
                                  {topupError}
                                </div>
                              )}
                              {loadingTopupPackages ? (
                                <div className="w-full px-2 py-1 border border-gray-300 rounded text-sm bg-gray-50">
                                  <span className="text-gray-500">Loading packages...</span>
                                </div>
                              ) : topupPackages.length > 0 ? (
                                <div className="space-y-2">
                                  <select
                                    value={selectedTopupPackage}
                                    onChange={(e) => setSelectedTopupPackage(e.target.value)}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  >
                                    {topupPackages.map((pkg, index) => (
                                      <option key={pkg.product_name || index} value={pkg.product_name}>
                                        {pkg.name} - €{pkg.gross_price_eur} ({pkg.data_amount}MB, {pkg.validity_days} days)
                                      </option>
                                    ))}
                                  </select>
                                  <button
                                    onClick={() => handleTopupSubmit(profile)}
                                    disabled={topupSubmitting || !selectedTopupPackage}
                                    className="w-full flex items-center justify-center px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <Save className="h-3 w-3 mr-1" />
                                    {topupSubmitting ? 'Saving...' : 'Save Renewal Top-Up Plan'}
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => loadTopupPackages(profile.iccid)}
                                  className="w-full flex items-center justify-center px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded hover:bg-gray-200"
                                >
                                  <Zap className="h-3 w-3 mr-1" />
                                  Load Renewal Plans
                                </button>
                              )}
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Renewal Count</span>
                            <div>
                              <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                {profile.renewal_count || 0}
                              </span>
                            </div>
                          </div>
                          <div>
                            <span className="font-medium">Auto-Renew</span>
                            <div className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={profile.is_auto_renew || false}
                                onChange={(e) => handleAutoRenewChange(profile, e.target.checked)}
                                disabled={saving}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                              />
                              <span className="text-sm text-gray-600">
                                {profile.is_auto_renew ? 'Enabled' : 'Disabled'}
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex space-x-2 mt-4">
                          <button 
                            className={`flex items-center px-3 py-2 rounded-md text-sm ${
                              userDetails.status === 'ARCHIVED'
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                            }`}
                            disabled={userDetails.status === 'ARCHIVED'}
                          >
                            <RotateCcw className="h-4 w-4 mr-2" />
                            Top-up
                          </button>
                          <button
                            onClick={onAddEsim}
                            className={`flex items-center px-3 py-2 rounded-md text-sm ${
                              userDetails.status === 'ARCHIVED' || hasActiveEsim()
                                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                            disabled={userDetails.status === 'ARCHIVED' || hasActiveEsim()}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Add eSIM
                          </button>
                        </div>
                      </div>
                    );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-500 mb-4">No active eSIM for this user</p>
                    <button
                      onClick={onAddEsim}
                      className={`flex items-center justify-center px-4 py-2 rounded-md mx-auto ${
                        userDetails.status === 'ARCHIVED' || hasActiveEsim()
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                      }`}
                      disabled={userDetails.status === 'ARCHIVED' || hasActiveEsim()}
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Add eSIM
                    </button>
                  </div>
                )}
              </div>

              {/* Documents Card */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Documents</h3>
                <div className="space-y-4">
                  {userDetails.profiles && userDetails.profiles.length > 0 ? (
                    userDetails.profiles.map((profile, index) => (
                      <div key={profile.name || index} className="flex items-start space-x-3">
                        <FileText className="h-5 w-5 text-gray-400 mt-1" />
                        <div className="flex-1">
                          <div className="font-medium text-sm text-gray-900">
                            eSIM QR Code {userDetails.profiles.length > 1 ? `#${index + 1}` : ''}
                          </div>
                          <div className="text-sm text-gray-600">
                            ICCID: {profile.iccid || 'N/A'} - QR code for scanning and activating the eSIM
                          </div>
                          <div className="flex space-x-2 mt-2">
                            <button 
                              onClick={() => handleViewPdf(profile)}
                              className="text-sm text-blue-600 hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
                              disabled={!profile.pdf_file}
                            >
                              View PDF
                            </button>
                            <button 
                              onClick={() => handleResendEmail(profile)}
                              className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                            >
                              <Mail className="h-3 w-3 mr-1" />
                              Resend Email
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-gray-500">No eSIM profiles available</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Column - History Timeline */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">History Timeline</h3>
                <div className="relative">
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                  <div className="space-y-6">
                    {userDetails.events && userDetails.events.length > 0 ? (
                      userDetails.events.map((event, index) => (
                        <div key={event.name} className="relative pl-10">
                          <div className="absolute left-0 top-1 flex items-center justify-center w-8 h-8 rounded-full bg-white border-2 border-gray-200">
                            {getEventIcon(event.event_type)}
                          </div>
                          <div className="pb-6">
                            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-1">
                              <h3 className="font-medium text-gray-900 capitalize">
                                {event.event_type.replace(/_/g, ' ')}
                              </h3>
                              <time className="text-sm text-gray-500">
                                {new Date(event.ts).toLocaleString()}
                              </time>
                            </div>
                            {event.data && (
                              <p className="text-gray-600 text-sm mb-2">
                                {JSON.parse(event.data).description || 'Event occurred'}
                              </p>
                            )}
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        No events found
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
